"use client";

import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Video,
  Volume2,
  VolumeX,
} from "lucide-react";
import type { ComponentRef, CSSProperties, ReactNode, SVGProps } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { MuxPlayer } from "@/components/post-rich-text/mux-player";
import { cn } from "@/lib/utils";

import { VS_FONT_FEATURES } from "./constants";
import type { HighlightClip } from "./data";
import { HIGHLIGHT_CLIPS, PANEL_LINKS } from "./data";
import { VsLightbox } from "./lightbox";

/** YouTube brand mark. */
function YoutubeLogo(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

/** Spotify brand mark. */
function SpotifyLogo(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
    </svg>
  );
}

/** Clips CTA buttons — mirror the hero's primary (filled) / secondary (ghost). */
const CTA_PRIMARY =
  "inline-flex h-10 items-center justify-center gap-2 rounded-full bg-[#001eff] px-4 text-[16px] leading-none font-medium text-white shadow-[0_4px_1px_-2px_rgba(0,0,0,0.06)] transition-colors hover:bg-[#1a1aff] motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]";
const CTA_SECONDARY =
  "inline-flex h-10 items-center justify-center gap-2 rounded-full px-4 text-[16px] leading-none font-medium text-white transition-opacity hover:opacity-80 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]";

interface VsHighlightsProps {
  className?: string;
  /**
   * Skip the id/eyebrow/heading/description block — used when this reel
   * strip is embedded under another component's heading (e.g. folded into
   * the combined "Gallery" section) rather than standing on its own.
   */
  hideHeading?: boolean;
}

/** Placeholder reels shown while no real clips are wired up yet. */
const PLACEHOLDER_CLIP_COUNT = 5;

/** Tile reel styling — fill the tile, cover-crop, hide all player controls. */
const REEL_STYLE = {
  "--controls": "none",
  "--media-object-fit": "cover",
  height: "100%",
  width: "100%",
} as CSSProperties;

/** Lightbox player styling — full controls, contain so nothing is cropped. */
const LIGHTBOX_STYLE = {
  "--media-object-fit": "contain",
  height: "100%",
  width: "100%",
} as CSSProperties;

const CONTROL_BTN =
  "pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/75 focus:outline-none focus-visible:ring-2 focus-visible:ring-white";

const SCROLL_ARROW_BTN =
  "inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#313335] text-white transition-colors hover:bg-white/5 motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]";

/**
 * Short-form clips row — a horizontal strip of vertical reels from both
 * summits. Driven by `HIGHLIGHT_CLIPS` in `data.ts` (London clips stream
 * from Mux via `playbackId`; New York's, uploaded straight to Sanity, play
 * from `src` instead — see `HighlightClip`).
 *
 * Each reel autoplays muted/looping once scrolled into view. Hovering (or
 * tapping on touch) reveals controls to unmute or expand the clip into a
 * near-fullscreen lightbox with full playback controls. Tiles are sized so
 * three sit in view at a time; left/right arrows below the strip scroll by
 * roughly one screen's worth, and the strip also scrolls directly (drag or
 * trackpad).
 *
 * Degrades gracefully: with no clips wired up it renders placeholder slots.
 *
 * Designed to sit inside a `PageRailSection` (the parent adds the horizontal
 * gutter), so this component only owns its vertical rhythm.
 */
export function VsHighlights({
  className,
  hideHeading = false,
}: VsHighlightsProps) {
  const hasClips = HIGHLIGHT_CLIPS.length > 0;
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  // Only one reel may be unmuted at a time (null = all muted).
  const [unmutedIndex, setUnmutedIndex] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const expanded =
    expandedIndex !== null ? HIGHLIGHT_CLIPS[expandedIndex] : null;
  const close = useCallback(() => setExpandedIndex(null), []);

  // Opening the spotlight mutes every inline reel, so the larger player is the
  // only possible audio source (no duplicate audio).
  const openExpanded = useCallback((i: number) => {
    setExpandedIndex(i);
    setUnmutedIndex(null);
  }, []);

  const scrollByDirection = useCallback((direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: "smooth" });
  }, []);

  return (
    <div
      id={hideHeading ? undefined : "clips"}
      style={{ fontFeatureSettings: VS_FONT_FEATURES }}
      className={cn(
        !hideHeading && "scroll-mt-[120px] py-[72px] max-md:py-12",
        className
      )}
    >
      {!hideHeading ? (
        <>
          <p className="mb-3 text-[12px] tracking-[0.0967em] text-[#95999d] uppercase">
            clips
          </p>
          <h2 className="mb-3 text-[40px] leading-[1.2] font-normal tracking-[-1.6px] text-white max-md:text-[32px] max-md:tracking-[-1.28px]">
            Snippets from the summits.
          </h2>
          <p className="mb-12 max-w-[640px] text-[14px] leading-[1.6] text-white/70 max-md:mb-9">
            Short clips captured around the panels and the venues, from both
            London and New York.
          </p>
        </>
      ) : null}

      {/* Horizontal reel strip — three in view, scroll (drag, trackpad, or
          the arrows below) for the rest. */}
      <div
        ref={scrollRef}
        className={cn(
          "flex gap-4 overflow-x-auto pb-2",
          "snap-x snap-mandatory",
          "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        )}
      >
        {hasClips
          ? HIGHLIGHT_CLIPS.map((clip, i) => (
              <ClipTile
                key={clip.playbackId ?? clip.src}
                clip={clip}
                muted={unmutedIndex !== i}
                onToggleMute={() =>
                  setUnmutedIndex((cur) => (cur === i ? null : i))
                }
                onExpand={() => openExpanded(i)}
              />
            ))
          : // TODO(visionaries): clip assets — these placeholder slots disappear
            // automatically once HIGHLIGHT_CLIPS has real clips in data.ts.
            Array.from({ length: PLACEHOLDER_CLIP_COUNT }).map((_, i) => (
              <div
                key={i}
                aria-hidden
                className="flex aspect-[9/16] w-[78vw] max-w-[300px] shrink-0 snap-start items-center justify-center rounded-2xl border border-[#313335] bg-[#171717] md:w-[calc((100%-2rem)/3)] md:max-w-none"
              >
                <Video size={24} strokeWidth={1.5} className="text-white/20" />
              </div>
            ))}
      </div>

      {/* Scroll hint — left/right arrows under the strip so it's clear it
          scrolls both ways. */}
      {hasClips ? (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => scrollByDirection(-1)}
            aria-label="Scroll clips left"
            className={SCROLL_ARROW_BTN}
          >
            <ChevronLeft size={18} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => scrollByDirection(1)}
            aria-label="Scroll clips right"
            className={SCROLL_ARROW_BTN}
          >
            <ChevronRight size={18} aria-hidden />
          </button>
        </div>
      ) : null}

      {/* Full-conversations CTA. Each button shows only when its URL is set in
          PANEL_LINKS (data.ts); the whole block is hidden until at least one is
          ready. */}
      {PANEL_LINKS.youtube || PANEL_LINKS.spotify ? (
        <div className="mt-10 flex flex-col items-start gap-5">
          <p className="text-[14px] leading-[1.6] text-white/70">
            Want the full conversations? Every panel is on YouTube and Spotify.
          </p>
          <div className="flex items-center gap-3 max-md:w-full max-md:flex-col max-md:items-stretch">
            {PANEL_LINKS.youtube ? (
              <a
                href={PANEL_LINKS.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className={CTA_PRIMARY}
              >
                <YoutubeLogo className="h-[18px] w-[18px]" />
                Watch the full panels
              </a>
            ) : null}
            {PANEL_LINKS.spotify ? (
              <a
                href={PANEL_LINKS.spotify}
                target="_blank"
                rel="noopener noreferrer"
                className={CTA_SECONDARY}
              >
                <SpotifyLogo className="h-[18px] w-[18px]" />
                Listen on Spotify
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Expanded lightbox (focus-trapped via the shared Dialog). */}
      <VsLightbox
        open={expanded !== null}
        onOpenChange={(o) => {
          if (!o) close();
        }}
        title="Clip viewer"
      >
        {expanded ? (
          <div className="aspect-[9/16] h-[85vh] max-w-[92vw] overflow-hidden rounded-xl bg-black">
            {expanded.playbackId ? (
              <MuxPlayer
                playbackId={expanded.playbackId}
                title={expanded.label ?? "Visionaries Summit clip"}
                autoPlay
                loop
                style={LIGHTBOX_STYLE}
              />
            ) : (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video
                src={expanded.src}
                className="h-full w-full object-contain"
                loop
                autoPlay
                playsInline
                controls
              />
            )}
          </div>
        ) : null}
      </VsLightbox>
    </div>
  );
}

interface ClipTileProps {
  clip: HighlightClip;
  /** Parent-controlled mute state (only one reel is unmuted at a time). */
  muted: boolean;
  onToggleMute: () => void;
  onExpand: () => void;
}

/** Dispatches to the Mux-streamed reel or the plain-file fallback, depending
 *  on which the clip has set (see `HighlightClip`). */
function ClipTile(props: ClipTileProps) {
  if (props.clip.playbackId) return <MuxClipTile {...props} />;
  return <FallbackClipTile {...props} />;
}

interface ClipTileChromeProps {
  label?: string;
  muted: boolean;
  onToggleMute: () => void;
  onExpand: () => void;
  onMouseEnter: () => void;
  children: ReactNode;
}

/** Shared reel chrome — the tile frame, click-to-expand hint, unmute
 *  control, and caption — wrapping whichever player element is passed in. */
function ClipTileChrome({
  label,
  muted,
  onToggleMute,
  onExpand,
  onMouseEnter,
  children,
}: ClipTileChromeProps) {
  return (
    <figure
      onMouseEnter={onMouseEnter}
      className="group relative aspect-[9/16] w-[78vw] max-w-[300px] shrink-0 snap-start overflow-hidden rounded-2xl bg-[#171717] md:w-[calc((100%-2rem)/3)] md:max-w-none"
    >
      {children}

      {/* Click anywhere on the reel to open the spotlight. A maximize hint
          fades in on hover (always shown on touch). */}
      <button
        type="button"
        onClick={onExpand}
        aria-label="Open clip in spotlight"
        className="absolute inset-0 z-[1] flex cursor-pointer items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset"
      >
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-black/45 text-white opacity-0 backdrop-blur-sm transition-opacity duration-200 group-hover:opacity-100 motion-reduce:transition-none max-md:opacity-100">
          <Maximize2 size={20} aria-hidden />
        </span>
      </button>

      {/* Unmute control. Visible by default on touch (no hover); on md+
          revealed on hover or when a control in the group receives keyboard
          focus (so the button is never focusable while invisible). Sits above
          the click-to-expand layer. */}
      <div className="pointer-events-none absolute inset-0 z-[2] flex items-end justify-end bg-gradient-to-t from-black/40 to-transparent p-3 opacity-100 transition-opacity duration-200 motion-reduce:transition-none md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100">
        <button
          type="button"
          onClick={onToggleMute}
          aria-label={muted ? "Unmute clip" : "Mute clip"}
          className={CONTROL_BTN}
        >
          {muted ? (
            <VolumeX size={16} aria-hidden />
          ) : (
            <Volume2 size={16} aria-hidden />
          )}
        </button>
      </div>

      {label ? (
        <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-[12px] font-medium text-white">
          {label}
        </figcaption>
      ) : null}
    </figure>
  );
}

/** A Mux-streamed reel — muted-autoplay with hover controls (unmute / expand). */
function MuxClipTile({ clip, muted, onToggleMute, onExpand }: ClipTileProps) {
  const playerRef = useRef<ComponentRef<typeof MuxPlayer>>(null);

  // Make sure the reel is playing (autoplay should already have started it;
  // this covers the case where the browser held off until interaction).
  const ensurePlaying = useCallback(() => {
    playerRef.current?.play?.()?.catch(() => {});
  }, []);

  // Keep the player's audio in sync with the parent-controlled muted state, so
  // unmuting one reel mutes every other.
  useEffect(() => {
    const el = playerRef.current;
    if (!el) return;
    el.muted = muted;
    if (!muted) el.play?.()?.catch(() => {});
  }, [muted]);

  return (
    <ClipTileChrome
      label={clip.label}
      muted={muted}
      onToggleMute={onToggleMute}
      onExpand={onExpand}
      onMouseEnter={ensurePlaying}
    >
      <MuxPlayer
        ref={playerRef}
        playbackId={clip.playbackId}
        title={clip.label ?? "Visionaries Summit clip"}
        autoPlay="muted"
        loop
        preload="metadata"
        className="absolute inset-0 h-full w-full"
        style={REEL_STYLE}
      />
    </ClipTileChrome>
  );
}

/** A plain-file reel (uploaded straight to Sanity, not yet on Mux) — same
 *  muted-autoplay behaviour and controls as `MuxClipTile`, via a native
 *  `<video>` element instead of the Mux player. */
function FallbackClipTile({
  clip,
  muted,
  onToggleMute,
  onExpand,
}: ClipTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const ensurePlaying = useCallback(() => {
    videoRef.current?.play()?.catch(() => {});
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = muted;
    if (!muted) el.play()?.catch(() => {});
  }, [muted]);

  return (
    <ClipTileChrome
      label={clip.label}
      muted={muted}
      onToggleMute={onToggleMute}
      onExpand={onExpand}
      onMouseEnter={ensurePlaying}
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        src={clip.src}
        muted
        loop
        autoPlay
        playsInline
        preload="metadata"
        className="absolute inset-0 h-full w-full object-cover"
      />
    </ClipTileChrome>
  );
}
